import { APIDetails } from './apis-details';

export const talentHubApi: APIDetails = {
  id: 'API_KEYS.talentHubApi',
  title: 'TalentHunt API',
  subtitle:
    'AI-powered talent sourcing, lead discovery, and job-candidate matching for smarter hiring decisions.',
  description: [
    'Recruiters and HR teams are under constant pressure to find the right talent faster while managing growing volumes of candidates and job roles. Manual screening, inconsistent job analysis, and fragmented lead tracking slow down hiring and increase the risk of missing top candidates.',
    'The TalentHunt API streamlines the entire talent discovery process. It helps teams quickly identify relevant candidates, understand key skills required for each role, and manage candidate leads in one seamless workflow. By automating repetitive tasks and improving candidate matching, TalentHunt enables smarter, faster, and more consistent hiring decisions.',
    'With improved efficiency and better visibility across job postings and candidate pipelines, TalentHunt empowers organizations to scale recruitment efforts, reduce time-to-hire, and build stronger teams with confidence.',
  ],
  overview: [
    'TalentHunt API streamlines the entire talent discovery and hiring workflow by combining AI-powered job analysis, candidate discovery, and lead management in one seamless platform.',
  ],
  features: [
    {
      title: 'Job Posting & Management',
      description:
        'Create, update, and retrieve job postings with AI-powered skill extraction for smarter recruitment.',
    },
    {
      title: 'Lead Discovery',
      description:
        'Automatically find potential candidates using job descriptions and AI engine integration.',
    },
    {
      title: 'Enhanced Processing',
      description:
        'Leverage advanced AI-driven filtering and candidate matching to ensure high-quality leads.',
    },
    {
      title: 'Lead Management',
      description:
        'Perform CRUD operations on leads — create, update, delete, and retrieve efficiently.',
    },
    {
      title: 'Integration Ready',
      description:
        'Seamlessly connect with ATS, HR platforms, or internal recruitment systems.',
    },
  ],
  workflow: [
    'Recruiter creates a job posting',
    'System process job description and discovered candidate leads via AI engine',
    'Leads processed and stored for the job',
    'Recruiters retrieve job details, leads, or perform updates/deletion',
  ],
  endpoints: [
    {
      category: 'Job Management',
      items: [
        {
          category: 'Create New Job',
          method: 'POST',
          path: '/api/v1/jobs/',
          description: 'Creates a new job posting.',
        },
        {
          category: 'Fetch Job Details',
          method: 'GET',
          path: '/api/v1/jobs/{job_id}',
          description:
            'Retrieves complete job details including title, description, skills, and processing status.',
        },
        {
          category: 'Update Job Skills',
          method: 'PUT',
          path: '/api/v1/jobs/{job_id}/skills',
          description: 'Updates the skills list for an existing job posting.',
        },
        {
          category: 'Get Job Leads',
          method: 'GET',
          path: '/api/v1/jobs/{job_id}/leads',
          description:
            'Retrieves all potential candidates (leads) for a specific job posting.',
        },
        {
          category: 'Process Job Leads',
          method: 'POST',
          path: '/api/v1/jobs/{job_id}/process',
          description: 'Get process status',
        },
      ],
    },
    {
      category: 'Lead Management',
      items: [
        {
          category: 'Create Custom Lead',
          method: 'POST',
          path: '/api/v1/leads/',
          description:
            'Creates a customized lead from dictionary data for a specific job posting.',
        },
        {
          category: 'Get Lead Details',
          method: 'GET',
          path: '/api/v1/leads/{lead_id}',
          description:
            'Retrieves complete information about a specific lead including contact details and qualifications.',
        },
        {
          category: 'List All Leads',
          method: 'GET',
          path: '/api/v1/leads/',
          description:
            'Retrieves leads from database with filtering by organization, department, sub-department, with pagination support.',
        },
        {
          category: 'Update Lead Info',
          method: 'PUT',
          path: '/api/v1/leads/{lead_id}',
          description:
            'Updates lead information with new data like contact details or qualifications.',
        },
        {
          category: 'Remove Lead',
          method: 'DELETE',
          path: '/api/v1/leads/{lead_id}',
          description: 'Permanently removes a lead from the system database.',
        },
      ],
    },
  ],
  apiDocumentation: [
    // Jobs Endpoints
    {
      title: 'Create Job',
      method: 'POST',
      path: '/jobs/',
      requestPath: 'POST /jobs/',
      description: 'Create a new job posting.',
      requestExample: {
        title: 'Frontend Developer',
        description:
          'Build and maintain responsive web applications using React.',
        company: 'Innovatech Solutions',
        location: 'New York, USA',
        skills: ['JavaScript', 'React', 'HTML', 'CSS'],
      },
      responseExample: {
        title: 'Frontend Developer',
        description:
          'Build and maintain responsive web applications using React.',
        company: 'Innovatech Solutions',
        location: 'New York, USA',
        skills: ['JavaScript', 'React', 'HTML', 'CSS'],
        job_id: 101,
        processing_status: 'pending',
        created_at: '2025-12-24T20:00:00.000Z',
        updated_at: '2025-12-24T20:00:00.000Z',
      },
      statusCode: '200',
    },
    {
      title: 'Get Job',
      method: 'GET',
      path: '/jobs/{job_id}',
      requestPath: 'GET /jobs/{job_id}',
      description:
        'Retrieve complete job details including title, description, skills, and processing status.',
      requestExample: {
        job_id: 101,
      },
      responseExample: {
        title: 'Frontend Developer',
        description:
          'Build and maintain responsive web applications using React.',
        company: 'Innovatech Solutions',
        location: 'New York, USA',
        skills: ['JavaScript', 'React', 'HTML', 'CSS'],
        job_id: 101,
        processing_status: 'pending',
        created_at: '2025-12-24T20:00:00.000Z',
        updated_at: '2025-12-24T20:10:00.000Z',
      },
      statusCode: '200',
    },
    {
      title: 'Update Job Skills',
      method: 'PUT',
      path: '/jobs/{job_id}/skills',
      requestPath: 'PUT /jobs/{job_id}/skills',
      description: 'Update the skills list for an existing job posting',
      requestExample: ['JavaScript', 'React', 'TypeScript', 'TailwindCSS'],
      responseExample: {
        title: 'Frontend Developer',
        description:
          'Build and maintain responsive web applications using React.',
        company: 'Innovatech Solutions',
        location: 'New York, USA',
        skills: ['JavaScript', 'React', 'TypeScript', 'TailwindCSS'],
        job_id: 101,
        processing_status: 'pending',
        created_at: '2025-12-24T20:00:00.000Z',
        updated_at: '2025-12-24T20:15:00.000Z',
      },
      statusCode: '200',
    },
    {
      title: 'Get Job Leads',
      method: 'GET',
      path: '/jobs/{job_id}/leads',
      requestPath: 'GET /jobs/{job_id}/leads',
      description:
        'Retrieve all potential candidates (leads) for a specific job posting',
      responseExample: [
        {
          first_name: 'Alice',
          last_name: 'Johnson',
          email: 'alice.johnson@example.com',
          title: 'Software Engineer',
          company: 'TechCorp',
          linkedin_url: 'https://linkedin.com/in/alicejohnson',
          phone: '+1234567890',
          location: 'San Francisco, USA',
          id: 'lead_001',
        },
      ],
      statusCode: '200',
    },
    {
      title: 'Process Job',
      method: 'POST',
      path: '/jobs/{job_id}/process',
      requestPath: 'POST /jobs/{job_id}/process',
      description: 'Start processing job leads and check status',
      responseExample: {
        job_id: 101,
        message: 'Processing started',
        status: 'pending',
        task_id: 'task_001',
      },
      statusCode: '200',
    },

    // Leads Endpoints
    {
      title: 'Create Lead',
      method: 'POST',
      path: '/leads/',
      requestPath: 'POST /leads/',
      description: 'Create a customized lead for a specific job posting',
      requestExample: {
        first_name: 'Alice',
        last_name: 'Johnson',
        email: 'alice.johnson@example.com',
        title: 'Software Engineer',
        company: 'TechCorp',
        linkedin_url: 'https://linkedin.com/in/alicejohnson',
        phone: '+1234567890',
        location: 'San Francisco, USA',
      },
      responseExample: {
        first_name: 'Alice',
        last_name: 'Johnson',
        email: 'alice.johnson@example.com',
        title: 'Software Engineer',
        company: 'TechCorp',
        linkedin_url: 'https://linkedin.com/in/alicejohnson',
        phone: '+1234567890',
        location: 'San Francisco, USA',
        id: 'lead_001',
        email_status: 'unverified',
        photo_url: 'https://example.com/photos/alice.jpg',
        headline: 'Full Stack Developer',
        seniority: 'Mid-level',
        organization_id: 'org_01',
        street_address: '123 Market Street',
        city: 'San Francisco',
        state: 'CA',
        country: 'USA',
        postal_code: '94105',
      },
      statusCode: '200',
    },
    {
      title: 'Get Leads',
      method: 'GET',
      path: '/leads/',
      requestPath:
        'GET /leads/?organization_id=&department=&sub_department=&limit=50&offset=0',
      description:
        'Retrieve leads from database with optional filtering and pagination',
      responseExample: [
        {
          first_name: 'Alice',
          last_name: 'Johnson',
          email: 'alice.johnson@example.com',
          title: 'Software Engineer',
          company: 'TechCorp',
          linkedin_url: 'https://linkedin.com/in/alicejohnson',
          phone: '+1234567890',
          location: 'San Francisco, USA',
          id: 'lead_001',
        },
        {
          first_name: 'Bob',
          last_name: 'Smith',
          email: 'bob.smith@example.com',
          title: 'Backend Developer',
          company: 'Innovatech Solutions',
          linkedin_url: 'https://linkedin.com/in/bobsmith',
          phone: '+1987654321',
          location: 'New York, USA',
          id: 'lead_002',
        },
      ],
      statusCode: '200',
    },
    {
      title: 'Get Lead by ID',
      method: 'GET',
      path: '/leads/{lead_id}',
      requestPath: 'GET /leads/{lead_id}',
      description: 'Retrieve complete information about a specific lead',
      responseExample: {
        first_name: 'Alice',
        last_name: 'Johnson',
        email: 'alice.johnson@example.com',
        title: 'Software Engineer',
        company: 'TechCorp',
        linkedin_url: 'https://linkedin.com/in/alicejohnson',
        phone: '+1234567890',
        location: 'San Francisco, USA',
        id: 'lead_001',
      },
      statusCode: '200',
    },
    {
      title: 'Update Lead',
      method: 'PUT',
      path: '/leads/{lead_id}',
      requestPath: 'PUT /leads/{lead_id}',
      description: 'Update lead information with new data',
      requestExample: {
        title: 'Senior Software Engineer',
        company: 'TechCorp',
      },
      responseExample: {
        first_name: 'Alice',
        last_name: 'Johnson',
        email: 'alice.johnson@example.com',
        title: 'Senior Software Engineer',
        company: 'TechCorp',
        linkedin_url: 'https://linkedin.com/in/alicejohnson',
        phone: '+1234567890',
        location: 'San Francisco, USA',
        id: 'lead_001',
      },
      statusCode: '200',
    },
    {
      title: 'Delete Lead',
      method: 'DELETE',
      path: '/leads/{lead_id}',
      requestPath: 'DELETE /leads/{lead_id}',
      description: 'Permanently remove a lead from the system database',
      responseExample: { message: 'Lead deleted successfully' },
      statusCode: '200',
    },
  ],
  useCases: [
    {
      title: 'Job Posting & Skill Extraction',
      description:
        'Automatically extract keywords and skills from job descriptions using AI.',
    },
    {
      title: 'Candidate Lead Discovery',
      description:
        'Identify potential candidates using AI Engine API and TalentHunt’s enhanced filtering.',
    },
    {
      title: 'Lead Management',
      description:
        'Create, update, view, and delete candidate leads in a centralized system.',
    },
    {
      title: 'Enhanced Matching & Processing',
      description:
        'Perform advanced AI-based lead discovery and filtering for better quality matches.',
    },
    {
      title: 'Integration with HR Platforms',
      description:
        'Integrate TalentHunt API into ATS or HR software to automate recruitment workflows.',
    },
  ],
  targetUsers: [
    'HR & Recruitment Teams – Automate candidate sourcing and lead management.',
    'Recruitment Agencies – Scale talent discovery across multiple job postings.',
    'Talent Platforms / SaaS Products – Integrate AI-driven job-to-candidate matching features.',
    'Enterprise HR Systems – Enhance structured hiring processes with AI-assisted workflows.',
    'Developers building recruitment tools – Use API endpoints to build custom hiring solutions.',
  ],
};
